import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useOidc } from '@nohara-dxdev/react-oidc';

import {
  PropertyRegistrationForm,
  ContractFactoryItem,
  FieldStaffItem,
} from '../types/property.types';
import { propertiesService } from '../services/properties.service';
import { mapFormToCreatePropertyRequest } from '../mappers/create-property-mapper';
import { useDepartments } from '../../companies/hooks/use-departments';
import { useStaffs } from '../../companies/hooks/use-staffs';
import { validatePropertyRegistrationForm } from '../validations/create-properties-validation';
import { companiesService } from '../../companies/services/companies.service';

import { useLoadingProgress } from '@/hooks/use-loading-progress';
import { FunctionName, PermissionAction, RoleKey } from '@/lib/enums';
import { checkPermission } from '@/utils/helpers';

interface ValidationError {
  field: string;
  message: string;
}

interface ValidationState {
  errors: ValidationError[];
  isValid: boolean;
  isSubmitting: boolean;
  touched: Set<string>;
}

const initialFormData: PropertyRegistrationForm = {
  // Basic Information
  wao_property_code: '',
  property_name: '',
  property_name_abbreviation: '',
  postal_code_first_half: '',
  postal_code_latter_half: '',
  address: '',
  start_date: '',
  owner_name: '',
  public_private_classification_code: undefined,
  customer_id: '',
  construction_company_id: '',
  construction_period_from: '',
  construction_period_to: '',
  office_address: '',
  office_tel: '',
  office_fax: '',
  structural_format_code: undefined,
  structural_format_others: '',
  number_of_floors_above_ground: '',
  number_of_basement_floors: '',
  site_manager_name: '',
  site_manager_tel: '',
  ready_mixed_concrete_contract_quantity: 0,
  vehicle_size: undefined,
  department_code: 0,
  sales_representative_code: '',
  delivery_person_code: '',

  // Onsite Staff Section
  ready_mix_concrete_staff_name: '',
  ready_mix_concrete_staff_tel: '',
  field_staffs: [],

  // Contract Factory Section
  contract_factories: [],

  // Information Input Section
  manager_comment: '',
  achievement_confirmation_company: '',
  check_method: undefined,
  nohara_direct: '',
  p_window: '',
  sellers_other_gc: '',
  memo: '',
  results_caution: '',
  jv_price: 0,
  general_contractor_purchase_price: 0,
  exists_purchase_order: undefined,
  cooperative: '',
  link_cement_code: undefined,
};

export const usePropertyRegistrationForm = (
  onSubmit?: (data: PropertyRegistrationForm) => Promise<void>
) => {
  const [formData, setFormData] = useState<PropertyRegistrationForm>(initialFormData);
  const [validationState, setValidationState] = useState<ValidationState>({
    errors: [],
    isValid: false,
    isSubmitting: false,
    touched: new Set<string>(),
  });
  const { setIsLoading } = useLoadingProgress();
  const { permissions } = useOidc();
  const [canManagePrice, setCanManagePrice] = useState<boolean>(false);
  // Companies state
  const [customers, setCustomers] = useState<unknown[]>([]);
  const [constructionCompanies, setConstructionCompanies] = useState<unknown[]>([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);

  // Ref to track if companies data has been loaded (prevent duplicate calls in StrictMode)
  const companiesLoadedRef = useRef(false);

  // Check if form has changes compared to initial data
  const hasFormChanges = useMemo(() => {
    return JSON.stringify(formData) !== JSON.stringify(initialFormData);
  }, [formData]);

  useEffect(() => {
    if (permissions) {
      setCanManagePrice(
        checkPermission(
          permissions,
          FunctionName.PROPERTY_MANAGEMENT,
          PermissionAction.MANAGE_PRICE
        )
      );
    }
  }, [permissions]);

  // Load customers and construction companies
  useEffect(() => {
    // Skip if already loaded
    if (companiesLoadedRef.current) {
      // Make sure loading is false when skipping
      setCompaniesLoading(false);
      return;
    }

    companiesLoadedRef.current = true; // Mark as loaded immediately
    let isMounted = true;

    const loadCompanies = async () => {
      try {
        setCompaniesLoading(true);

        // Load both in parallel for better performance
        const [customersResponse, constructionCompaniesResponse] = await Promise.all([
          companiesService.getCompaniesByPartnerClassification(1, FunctionName.PROPERTY_MANAGEMENT),
          companiesService.getCompaniesByPartnerClassification(3, FunctionName.PROPERTY_MANAGEMENT),
        ]);

        const customersData = Array.isArray(customersResponse.data.data)
          ? customersResponse.data.data
          : [];

        const constructionCompaniesData = Array.isArray(constructionCompaniesResponse.data.data)
          ? constructionCompaniesResponse.data.data
          : [];

        // Set data regardless of mount status to handle StrictMode double-mounting
        setCustomers(customersData);
        setConstructionCompanies(constructionCompaniesData);
      } catch (err) {
        if (isMounted) {
          console.error('Error loading companies:', err);
          companiesLoadedRef.current = false; // Reset on error to allow retry
        }
      } finally {
        // Always set loading to false, regardless of mount status
        // This prevents the loading state from being stuck
        setCompaniesLoading(false);
      }
    };

    void loadCompanies();

    return () => {
      isMounted = false;
    };
  }, []); // Empty dependency - only load once on mount

  // Mark all required fields as touched on initial load to show validation errors immediately
  useEffect(() => {
    const requiredFields = [
      'property_name',
      'property_name_abbreviation',
      'address',
      'start_date',
      'customer_id',
      'construction_company_id',
      'department_code',
      'sales_representative_code',
      'delivery_person_code',
    ];

    setValidationState((prev) => ({
      ...prev,
      touched: new Set([...prev.touched, ...requiredFields]),
    }));
  }, []);

  // Hooks for departments and staffs
  const { departments, loading: departmentsLoading } = useDepartments(PermissionAction.CREATE);
  const { loading: staffsLoading, loadStaffsByDepartment, getStaffsByDepartment } = useStaffs();

  // Use the comprehensive validation system
  const validation = useMemo(() => {
    return validatePropertyRegistrationForm(formData, validationState.touched);
  }, [formData, validationState.touched]);

  // Update validation state when form data changes
  useEffect(() => {
    setValidationState((prev) => ({
      ...prev,
      errors: validation.errors,
      isValid: validation.isValid,
    }));
  }, [validation]);

  // Update field value
  const updateField = useCallback(
    (field: keyof PropertyRegistrationForm, value: string | number | boolean | undefined) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));

      // For postal code fields, handle touched state based on values
      if (field === 'postal_code_first_half' || field === 'postal_code_latter_half') {
        const firstHalf =
          field === 'postal_code_first_half' ? value : formData.postal_code_first_half;
        const latterHalf =
          field === 'postal_code_latter_half' ? value : formData.postal_code_latter_half;

        // If both fields are empty, remove them from touched state
        if (
          (!firstHalf || firstHalf === 0 || firstHalf === '') &&
          (!latterHalf || latterHalf === 0 || latterHalf === '')
        ) {
          setValidationState((prev) => {
            const newTouched = new Set(prev.touched);
            newTouched.delete('postal_code_first_half');
            newTouched.delete('postal_code_latter_half');
            return {
              ...prev,
              touched: newTouched,
            };
          });
        } else {
          // If at least one field has value, mark both as touched
          setValidationState((prev) => ({
            ...prev,
            touched: new Set([
              ...prev.touched,
              'postal_code_first_half',
              'postal_code_latter_half',
            ]),
          }));
        }
      } else {
        // For other fields, mark as touched
        setValidationState((prev) => ({
          ...prev,
          touched: new Set([...prev.touched, field]),
        }));
      }

      // Clear error for this field
      setValidationState((prev) => ({
        ...prev,
        errors: prev.errors.filter((error) => error.field !== field),
      }));
    },
    [formData.postal_code_first_half, formData.postal_code_latter_half]
  );

  // Field staff management (現場担当者)
  const addFieldStaff = useCallback(() => {
    const newStaff: FieldStaffItem = {
      name: '',
      tel: '',
      email: '',
    };
    setFormData((prev) => ({
      ...prev,
      field_staffs: [...(prev.field_staffs || []), newStaff],
    }));
  }, []);

  const updateFieldStaff = useCallback(
    (index: number, field: keyof FieldStaffItem, value: string | number) => {
      setFormData((prev) => ({
        ...prev,
        field_staffs:
          prev.field_staffs?.map((staff, idx) =>
            idx === index ? { ...staff, [field]: value } : staff
          ) || [],
      }));

      // Mark field as touched for validation
      setValidationState((prev) => ({
        ...prev,
        touched: new Set([...prev.touched, `field_staffs.${index}.${field}`]),
      }));
    },
    []
  );

  const removeFieldStaff = useCallback((index: number) => {
    setFormData((prev) => ({
      ...prev,
      field_staffs: prev.field_staffs?.filter((_, idx) => idx !== index) || [],
    }));
  }, []);

  // Contract factory management (契約工場)
  const addContractFactory = useCallback(() => {
    const newFactory: ContractFactoryItem = {
      factory: '',
      delivery_classification: '',
      quantity: 0,
    };
    setFormData((prev) => ({
      ...prev,
      contract_factories: [...(prev.contract_factories || []), newFactory],
    }));
  }, []);

  const updateContractFactory = useCallback(
    (index: number, field: keyof ContractFactoryItem, value: string | number) => {
      setFormData((prev) => ({
        ...prev,
        contract_factories:
          prev.contract_factories?.map((factory, idx) =>
            idx === index ? { ...factory, [field]: value } : factory
          ) || [],
      }));

      // Mark field as touched for validation
      setValidationState((prev) => ({
        ...prev,
        touched: new Set([...prev.touched, `contract_factories.${index}.${field}`]),
      }));
    },
    []
  );

  const removeContractFactory = useCallback((index: number) => {
    setFormData((prev) => ({
      ...prev,
      contract_factories: prev.contract_factories?.filter((_, idx) => idx !== index) || [],
    }));
  }, []);

  // Staff management
  const handleDepartmentChange = useCallback(
    (departmentId: string) => {
      // Always load both sales and delivery staffs for the department
      // The loadStaffsByDepartment function already has internal caching logic
      void loadStaffsByDepartment(RoleKey.SALE, departmentId, FunctionName.PROPERTY_MANAGEMENT);
      void loadStaffsByDepartment(
        RoleKey.DELIVERY_AND_OUTSOURCING_DELIVERY,
        departmentId,
        FunctionName.PROPERTY_MANAGEMENT
      );
    },
    [loadStaffsByDepartment]
  );

  // Helper function to convert department code to ID
  const findDepartmentIdByCode = (code: number): string => {
    // Handle both string and number comparison for type safety
    const department = departments.find((dept) => {
      return (
        dept.code === code ||
        dept.code?.toString() === code.toString() ||
        Number(dept.code) === code
      );
    });

    return department?.id || '';
  };

  // Get staffs for current department
  const departmentId = formData.department_code
    ? findDepartmentIdByCode(formData.department_code)
    : '';
  const salesStaffs = departmentId ? getStaffsByDepartment(departmentId, RoleKey.SALE) : [];
  const deliveryStaffs = departmentId
    ? getStaffsByDepartment(departmentId, RoleKey.DELIVERY_AND_OUTSOURCING_DELIVERY)
    : [];

  // Field validation helpers
  const getFieldError = useCallback(
    (field: string): string => {
      // Only show validation errors for touched fields
      if (!validationState.touched.has(field)) {
        return '';
      }
      const error = validationState.errors.find((err) => err.field === field);
      return error?.message || '';
    },
    [validationState.errors, validationState.touched]
  );

  const isFieldValid = useCallback(
    (field: string): boolean => {
      // Only show validation errors for touched fields
      if (!validationState.touched.has(field)) {
        return true;
      }
      return !validationState.errors.some((err) => err.field === field);
    },
    [validationState.errors, validationState.touched]
  );

  // Form submission
  const handleSubmit = useCallback(async (): Promise<boolean> => {
    setValidationState((prev) => ({ ...prev, isSubmitting: true }));
    setIsLoading(true);
    try {
      if (!validation.isValid) {
        setValidationState((prev) => ({ ...prev, isSubmitting: false }));
        setIsLoading(false);
        return false;
      }

      // Transform data to API format using mapper
      const apiData = mapFormToCreatePropertyRequest(formData, canManagePrice);

      // Call API
      await propertiesService.createProperty(apiData);

      // Call custom onSubmit callback if provided
      if (onSubmit) {
        await onSubmit(formData);
      }

      setValidationState((prev) => ({ ...prev, isSubmitting: false }));
      setIsLoading(false);
      // Return true to indicate success - component will handle navigation
      return true;
    } catch (error) {
      console.error('Form submission error:', error);
      const errorMessage =
        (error as Error)?.message || '登録に失敗しました。もう一度お試しください。';
      setValidationState((prev) => ({
        ...prev,
        isSubmitting: false,
        errors: [...prev.errors, { field: 'general', message: errorMessage }],
      }));
      setIsLoading(false);
      return false;
    }
  }, [formData, validation, onSubmit]);

  // Reset form
  const resetForm = useCallback(() => {
    setFormData(initialFormData);
    setValidationState({
      errors: [],
      isValid: false,
      isSubmitting: false,
      touched: new Set<string>(),
    });
    setIsLoading(false);
  }, []);

  // Note: Staff loading is handled by property-representatives-section.tsx when user selects department

  return {
    formData,
    validationState,
    updateField,
    addFieldStaff,
    updateFieldStaff,
    removeFieldStaff,
    addContractFactory,
    updateContractFactory,
    removeContractFactory,
    handleDepartmentChange,
    getFieldError,
    isFieldValid,
    handleSubmit,
    resetForm,
    hasFormChanges,
    departments,
    departmentsLoading,
    salesStaffs,
    deliveryStaffs,
    staffsLoading,
    customers,
    constructionCompanies,
    companiesLoading,
    canManagePrice,
  };
};
